/**
 * CableRequirementsTable Component
 *
 * Displays aggregated cable requirements grouped by floor plan.
 */

import React, { useMemo } from 'react';
import { Cable } from 'lucide-react';
import { ReportTable, ReportSection, type ReportTableColumn } from '../primitives/ReportTable';
import type { CableRequirementRow } from '@/types/reports';

interface CableRequirementsTableProps {
  data: CableRequirementRow[];
  groupByFloorPlan?: boolean;
}

export function CableRequirementsTable({
  data,
  groupByFloorPlan = true,
}: CableRequirementsTableProps) {
  const columns: ReportTableColumn<CableRequirementRow>[] = useMemo(
    () => [
      {
        key: 'label',
        header: 'Cable Type',
        render: row => (
          <span className="font-medium">{row.label}</span>
        ),
      },
      {
        key: 'category',
        header: 'Category',
        render: row => (
          <span className="text-muted-foreground">{row.category}</span>
        ),
        hideOnMobile: true,
      },
      {
        key: 'total_length',
        header: 'Total Length',
        align: 'right',
        render: row => (
          <span>
            {row.total_length.toLocaleString(undefined, { maximumFractionDigits: 1 })} {row.unit}
          </span>
        ),
      },
      {
        key: 'path_count',
        header: 'Paths',
        align: 'center',
        width: '80px',
      },
    ],
    []
  );

  // Group data by floor plan if requested
  const groupedData = useMemo(() => {
    if (!groupByFloorPlan) {
      return [{ floorPlanName: null, items: data }];
    }

    const groups = new Map<string, CableRequirementRow[]>();
    for (const row of data) {
      const key = row.floorPlanName ?? 'Unknown';
      const existing = groups.get(key) ?? [];
      existing.push(row);
      groups.set(key, existing);
    }

    return Array.from(groups.entries()).map(([name, items]) => ({
      floorPlanName: name,
      items,
    }));
  }, [data, groupByFloorPlan]);

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Cable className="h-12 w-12 mb-4" />
        <p>No cable requirements found</p>
        <p className="text-sm">Add cables to your paths to see requirements here</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupedData.map((group, index) => (
        <ReportSection
          key={group.floorPlanName ?? index}
          title={groupByFloorPlan && group.floorPlanName ? group.floorPlanName : undefined}
          avoidPageBreak
        >
          <ReportTable
            data={group.items}
            columns={columns}
            keyExtractor={(row, i) => `${row.identifier}-${i}`}
            emptyMessage="No cables in this floor plan"
            emptyIcon={<Cable className="h-8 w-8" />}
          />
        </ReportSection>
      ))}
    </div>
  );
}
